/**
 * SUBMISSION KEYS — the caller's own name for one act.
 *
 * Two of the three acts in this product take one (`StartWorkRequest`,
 * `AnswerRequestRequest`), and the contract says why: "so a double-click is
 * one start and not two". The key must therefore be STABLE for as long as the
 * act is the same act, and NEW the moment the act changes — a fresh key on
 * every render would defeat the guard entirely, and a single key for the
 * lifetime of the screen would make a second, genuinely different submission
 * silently return the first one's outcome.
 *
 * So keys are minted against a description of the act and remembered. The
 * description is built by the caller from whatever makes the act what it is:
 * which control account, which records were cited.
 */
import { useRef } from "react";

export interface Keyring {
  /** The key for this act. The same description always answers the same key. */
  (act: string): string;
}

export function useKeyring(): Keyring {
  const minted = useRef(new Map<string, string>());
  return useRef<Keyring>((act: string) => {
    const existing = minted.current.get(act);
    if (existing) return existing;
    const key = window.crypto.randomUUID();
    minted.current.set(act, key);
    return key;
  }).current;
}
